import React, { useEffect, useMemo, useState } from 'react';
import { PasswordStrength } from 'tai-password-strength';

import Field from '@/components/forms/Field';

import styles from './styles.module.scss';

const PasswordTester = new PasswordStrength();

interface Props {
  onStatusChange: (disableForm: boolean) => any;
  formik?: boolean;
  callback?: (key: string, value: string) => void;
}

const PasswordInputs = ({ onStatusChange, formik, callback }: Props) => {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const handlePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    callback && callback('password', e.target.value);
  };

  const handlePasswordConfirmation = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordConfirmation(e.target.value);
    callback && callback('newPassword', e.target.value);
  };

  const passwordsMatch = useMemo(() => {
    return password === passwordConfirmation;
  }, [password, passwordConfirmation]);

  const passwordStrength = useMemo(() => {
    return PasswordTester.check(password);
  }, [password]);

  const weakPassword = useMemo(() => {
    return passwordStrength.nistEntropyBits < 15;
  }, [passwordStrength.nistEntropyBits]);

  const StrengthName = useMemo(() => {
    const bits = passwordStrength.nistEntropyBits;
    if (bits <= 10) {
      return 'VERY_WEAK';
    } else if (bits <= 15) {
      return 'WEAK';
    } else if (bits <= 25) {
      return 'REASONABLE';
    } else if (bits <= 30) {
      return 'STRONG';
    } else {
      return 'VERY_STRONG';
    }
  }, [passwordStrength.nistEntropyBits]);

  useEffect(() => {
    const shouldDisableForm = !passwordsMatch || weakPassword;
    onStatusChange(shouldDisableForm);
  }, [onStatusChange, passwordsMatch, weakPassword]);

  return (
    <>
      <Field
        data-testid="password-field"
        name="password"
        label="New Password"
        type="password"
        value={password}
        className={styles.input}
        onChange={handlePassword}
        required
        color={passwordStrength.strengthCode && PasswordInputs.StrengthColorMap[StrengthName]}
        formik={formik}
      />
      {passwordStrength.strengthCode && (
        <div className={styles.strength} data-testid="strength" data-test-strength={StrengthName}>
          <span data-color={PasswordInputs.StrengthColorMap[StrengthName]}>
            {PasswordInputs.StrengthMessage[StrengthName]}
          </span>
        </div>
      )}
      <div className={styles.requirements}>
        <span>
          Password should:
          <ul>
            <li>Be more than 8 characters long</li>
            <li>Include at least 3 of the following:</li>
            <ul>
              <li>An uppercase character</li>
              <li>A lowercase character</li>
              <li>A number</li>
              <li>A special character</li>
            </ul>
          </ul>
        </span>
      </div>
      <Field
        type="password"
        name="newPassword"
        label="Confirm new password"
        value={passwordConfirmation}
        onChange={handlePasswordConfirmation}
        required
        formik={formik}
        data-testid="newPassword-field"
      />
      {!passwordsMatch && !!password && !!passwordConfirmation && (
        <div data-testid="match" className={styles.noMatch}>
          Passwords do not match
        </div>
      )}
    </>
  );
};

PasswordInputs.StrengthColorMap = {
  VERY_WEAK: 'red',
  WEAK: 'red',
  REASONABLE: 'green',
  STRONG: 'green',
  VERY_STRONG: 'green',
} as const;

PasswordInputs.StrengthMessage = {
  VERY_WEAK: 'Password is too short. Must be at least 8 characters.',
  WEAK: 'Password is too weak. Try making it harder to guess by using a random password generator or passphrase.',
  REASONABLE: 'Password is Average.',
  STRONG: 'Password is Good.',
  VERY_STRONG: 'Password is Strong.',
} as const;

export default PasswordInputs;
